/**
 * Which Claude model investigates, and which ones it may (IM-208).
 *
 * Claude runs on Bedrock in Qrvey's own control-plane account (decision,
 * 2026-10-27): data stays in AWS, access is IAM, every call is in CloudTrail,
 * and there is no API key to hold.
 *
 * ## Two things, deliberately apart
 *
 * - **The allowed models are code**, `INVESTIGATOR_MODELS`, because IAM names
 *   them: the worker may invoke exactly these inference profiles, and their
 *   models only through them. Widening that set is a reviewed deploy, as any
 *   permission is.
 * - **Which of them runs is configuration**, `/qim/{env}/investigator-model`,
 *   read on every investigation (the IM-123 pattern). Moving to another
 *   allowed model is an edit of one parameter and needs no release.
 *
 * A configured value outside the list stops the investigation with a sentence
 * saying so, rather than reaching Bedrock and coming back as AccessDenied — and
 * a failed read stops it too, rather than falling back to a model compiled in
 * here, which could contradict the parameter somebody just changed.
 *
 * ## InvokeModel, through a US profile (Amit, 2026-11-02)
 *
 * The SDK's Messages-API Bedrock endpoint authorises
 * `bedrock-mantle:CreateInference`, which AWS documents only on `*`; the
 * `bedrock-runtime` endpoint authorises `bedrock:InvokeModel` on a named
 * inference profile, so the policy can name exactly what may run. The same
 * models are served either way. US profiles keep a transcript — which carries a
 * customer's cluster details — in US regions, for AWS's ten percent.
 */
/** What may be configured, and what IAM grants. The first is the default. */
export const INVESTIGATOR_MODELS = [
    'us.anthropic.claude-opus-5-5',
    'us.anthropic.claude-sonnet-5-5',
];
export const DEFAULT_INVESTIGATOR_MODEL = INVESTIGATOR_MODELS[0];
/** A geography-prefixed inference profile id, as Bedrock names one. */
const PROFILE_ID = /^(us|eu|apac|jp|au|global)\.(anthropic\.claude-[a-z0-9-]+)$/;
/** The foundation model a profile routes to: `us.anthropic.x` is `anthropic.x`. */
export const foundationModelOf = (profile) => PROFILE_ID.exec(profile)?.[2];
/** Why a configured value cannot run, or nothing when it can. */
export const investigatorModelProblem = (value) => {
    const trimmed = value?.trim() ?? '';
    if (trimmed === '')
        return 'No investigator model is configured.';
    if (!PROFILE_ID.test(trimmed)) {
        return `The configured investigator model, ${trimmed}, is not a Bedrock inference profile id.`;
    }
    if (!INVESTIGATOR_MODELS.includes(trimmed)) {
        return `The configured investigator model, ${trimmed}, is not one this control plane may invoke (${INVESTIGATOR_MODELS.join(', ')}).`;
    }
    return undefined;
};
//# sourceMappingURL=model.js.map